import { EnumOption } from "@data-root/types";
import { FC, useCallback, useMemo, useState } from "react";
import { Button, Checkbox, Input, List, Space, Layout } from "antd";
import { SEARCH } from "../i18n/en";
import useDebouncedCallback from "../hooks/useDebouncedCallback";

export interface SelectProps {
  multiple: boolean;

  value: string | string[];
  setValue: (value: string | string[], displayValue: string) => void;

  items: EnumOption[];
  onQuery?: (query: string) => void;

  pinned?: EnumOption[];
}

export interface SelectSingleProps extends SelectProps {
  value: string;
  setValue: (value: string, displayValue: string) => void;
}

export interface SelectMultipleProps extends SelectProps {
  value: string[];
  setValue: (value: string[], displayValue: string) => void;
}

const { Header, Content } = Layout;

export const SelectSingle: FC<SelectSingleProps> = ({
  items,
  value,
  setValue,

  onQuery,

  pinned,
}) => {
  const sortedItems = useMemo(() => {
    return items.slice().sort((a, b) => {
      if (a.name === "" && b.name !== "") {
        return 1;
      } else if (a.name !== "" && b.name === "") {
        return -1;
      } else {
        return 0;
      }
    });
  }, [items]);
  const debouncedOnQuery = useDebouncedCallback(onQuery, 500);

  return (
    <Layout>
      <Header>
        <Input.Search
          placeholder={SEARCH}
          onSearch={onQuery}
          onChange={(e) => debouncedOnQuery(e.target.value)}
        />
      </Header>

      <Content>
        <Layout>
          {pinned && pinned.length > 0 && (
            <Header>
              <Space>
                {pinned.map(({ id, name }) => (
                  <Button
                    key={id}
                    type={value === id ? "primary" : "default"}
                    onClick={() => setValue(id, name)}
                  >
                    {name}
                  </Button>
                ))}
              </Space>
            </Header>
          )}

          <Content style={{ height: "400px", overflowY: "scroll" }}>
            <List
              split={false}
              dataSource={sortedItems}
              rowKey={(item) => item.id}
              renderItem={(item) => (
                <List.Item>
                  <Checkbox
                    checked={value === item.id}
                    onChange={(event) => {
                      if (event.target.checked) setValue(item.id, item.name);
                      else setValue("", "");
                    }}
                  >
                    {item.name}
                  </Checkbox>
                </List.Item>
              )}
            />
          </Content>
        </Layout>
      </Content>
    </Layout>
  );
};

export const SelectMultiple: FC<SelectMultipleProps> = ({
  items,
  value,
  setValue,
  onQuery,
  pinned,
}) => {
  const formattedItems = items.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const sortedItems = useMemo(() => {
    return formattedItems.slice().sort((a, b) => {
      if (a.label === "" && b.label !== "") {
        return 1;
      } else if (a.label !== "" && b.label === "") {
        return -1;
      } else {
        return 0;
      }
    });
  }, [formattedItems]);

  const valueToName = Object.fromEntries(
    sortedItems.map(({ label, value }) => [value, label])
  );
  const debouncedOnQuery = useDebouncedCallback(onQuery, 500);

  return (
    <Layout>
      <Header>
        <Input.Search
          placeholder={SEARCH}
          onSearch={onQuery}
          onChange={(e) => debouncedOnQuery(e.target.value)}
        />
      </Header>

      <Content>
        <Layout>
          <Header>
            {pinned && pinned.length > 0 && (
              <Space>
                {pinned.map(({ id, name }) => (
                  <Button
                    key={id}
                    type={value?.includes(id) ? "primary" : "default"}
                    onClick={() =>
                      setValue(value?.includes(id) ? [] : [id], name)
                    }
                  >
                    {name}
                  </Button>
                ))}
              </Space>
            )}
          </Header>

          <Content style={{ height: "400px", overflowY: "scroll" }}>
            <Checkbox.Group
              style={{ display: "flex", flexDirection: "column" }}
              value={value}
              onChange={(values) => {
                const names = values.map(
                  (value) => valueToName[value as string]
                );
                setValue(values as string[], names.join(", "));
              }}
              options={sortedItems}
            />
          </Content>
        </Layout>
      </Content>
    </Layout>
  );
};

export const Select: FC<SelectProps> = ({ multiple, ...props }) => {
  return multiple ? (
    <SelectMultiple {...(props as any)} />
  ) : (
    <SelectSingle {...(props as any)} />
  );
};
